/**
 * Phase 20 — "Right now": the stop-loss as it is being edited, judged on
 * today's sends of the group (or the container's own) with the stats the
 * last check collected — each condition's actual value against its
 * threshold, then what the rule would do. Nothing acts from here.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Check, Minus, X } from 'lucide-react';
import type { DropGenStopLossCheck, DropGenStopLossPreview } from '@trafficpulse/shared';

import { GATE_METRIC_OPTIONS } from '@/features/drop-generator/components/editor/verdicts/condition/gate-metric-options';
import { cn, getApiErrorMessage } from '@/lib/utils';

import { dkThenLocal } from '../../board-header/autosend/autosend-fact';
import { valueLabel } from '../verdicts/condition/metric-meanings';

const LABEL = new Map<string, string>(GATE_METRIC_OPTIONS.map((o) => [o.value, o.label]));
const n = (v: number) => v.toLocaleString('en-US');

/** One word for the step's closed line. */
export function rightNowSummary(preview: DropGenStopLossPreview | undefined): string {
  const v = preview?.check?.verdict;
  if (v === 'FAIL') return 'the rule holds — it would halt';
  if (v === 'PASS') return 'would not halt';
  return 'not judged yet';
}

const Verdict: FC<{ check: DropGenStopLossCheck; whose: string }> = ({ check, whose }) => {
  if (check.verdict === 'FAIL')
    return <p className="font-semibold text-destructive">→ The rule holds: it would halt {whose} for the rest of today.</p>;
  if (check.verdict === 'PASS') return <p className="font-semibold text-success">→ Would not halt — the rule does not hold today.</p>;
  return <p className="text-muted-foreground">→ Never a halt yet: {check.reason}.</p>;
};

const Conditions: FC<{ check: DropGenStopLossCheck }> = ({ check }) => (
  <ul className="flex flex-col gap-1">
    {check.conditions.map((c, i) => {
      const Icon = c.actual === null ? Minus : c.passed ? X : Check; // a holding condition is the bad news
      return (
        <li key={`${c.metric}-${i}`} className="flex flex-wrap items-center gap-x-2">
          <Icon className={cn('h-3.5 w-3.5 shrink-0', c.actual === null ? 'text-muted-foreground' : c.passed ? 'text-destructive' : 'text-success')} />
          <span className="font-medium">{LABEL.get(c.metric) ?? c.metric}</span>
          <span className="font-mono tabular-nums">{c.actual === null ? 'no value' : valueLabel(c.metric, c.actual)}</span>
          <span className="text-muted-foreground">
            {c.actual === null ? '— never counts as bad' : `${c.passed ? 'holds' : 'does not hold'}: ${c.op} ${valueLabel(c.metric, c.value)}`}
          </span>
        </li>
      );
    })}
  </ul>
);

export const RightNowCard: FC<{
  saved: boolean;
  hasRule: boolean;
  whose: string;
  tz: string | null;
  query: { data?: DropGenStopLossPreview; isError: boolean; error: unknown; isFetching: boolean; pending: boolean };
}> = ({ saved, hasRule, whose, tz, query }) => {
  if (!saved) return <p className="text-xs text-muted-foreground">Save it first — then today’s sends are judged here.</p>;
  if (!hasRule) return <p className="text-xs text-muted-foreground">Add a stop-loss condition — today’s numbers are then judged against it here.</p>;
  if (query.isError) return <p className="text-xs text-destructive">Could not judge today — {getApiErrorMessage(query.error)}</p>;
  const p = query.data;
  if (!p) return <div className="h-12 animate-pulse rounded-md bg-muted/60" />;
  if (!p.check) return <p className="text-xs text-muted-foreground">Nothing laid out today yet — once its drafts go, today’s numbers show here.</p>;
  const missing = p.sentDrops - p.collectedDrops;
  return (
    <div className={cn('flex flex-col gap-2 text-xs transition-opacity', (query.isFetching || query.pending) && 'opacity-60')}>
      <p className="text-muted-foreground">
        <span className="font-mono text-foreground">{n(p.check.smsSettled)}</span> settled SMS across {n(p.check.dropsSettled)} drop{p.check.dropsSettled === 1 ? '' : 's'}
        {missing > 0 ? ` · ${n(missing)} send${missing === 1 ? '' : 's'} not collected yet` : ''}
        {p.statsAt ? ` · as of ${dkThenLocal(p.statsAt, tz)}` : ''}
      </p>
      {p.check.conditions.length > 0 ? <Conditions check={p.check} /> : null}
      <Verdict check={p.check} whose={whose} />
      <p className="text-[11px] text-muted-foreground">Judged with the rule as you are editing it — Save makes it the one that acts. Nothing acts from here.</p>
    </div>
  );
};
